import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Chip,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButtonGroup,
  ToggleButton,
  Alert
} from '@mui/material';
import {
  Dashboard,
  ArrowBack,
  TrendingUp,
  TrendingDown,
  School,
  AccountBalance,
  Person,
  Warning,
  EmojiEvents,
  BarChart as BarChartIcon,
  SmartToy as SmartToyIcon,
  ChildCare as DaycareIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import principalAPI from '../../services/principalAPI';
import { useAuth } from '../../context/AuthContext';
import { chatbotAPI } from '../../services/api';

const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#fa709a', '#fee140', '#30b0fe', '#a3f7bf'];
const GRADE_COLORS = { A: '#4caf50', B: '#8bc34a', C: '#ff9800', D: '#ff5722', F: '#f44336' };

const perfColor = (pct) =>
  pct >= 75 ? '#4caf50' : pct >= 50 ? '#ff9800' : '#f44336';

const fmtPct = (v) => `${v}%`;

const formatCurrency = (amount) => {
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
};

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const nDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const today = () => new Date().toISOString().split('T')[0];

// Bucket marks percentage into grade
const gradeOf = (pct) => {
  if (pct >= 90) return 'A';
  if (pct >= 75) return 'B';
  if (pct >= 50) return 'C';
  if (pct >= 35) return 'D';
  return 'F';
};

// Compute subject-level averages from marks array
const computeSubjectPerf = (marks) => {
  const map = {};
  marks.forEach(m => {
    const subj = m.class_subjects?.subject_name || 'Unknown';
    if (!map[subj]) map[subj] = { total: 0, maxTotal: 0, count: 0 };
    map[subj].total += Number(m.marks || 0);
    map[subj].maxTotal += Number(m.max_marks || 100);
    map[subj].count += 1;
  });
  return Object.entries(map)
    .map(([name, d]) => ({
      name,
      avgPct: d.maxTotal > 0 ? Math.round((d.total / d.maxTotal) * 100) : 0,
      count: d.count
    }))
    .sort((a, b) => b.avgPct - a.avgPct);
};

// Compute class-level averages from marks array, enriched with class name
const computeClassPerf = (marks, classes) => {
  const map = {};
  marks.forEach(m => {
    const cid = m.class_id;
    if (!cid) return;
    if (!map[cid]) map[cid] = { total: 0, maxTotal: 0, count: 0 };
    map[cid].total += Number(m.marks || 0);
    map[cid].maxTotal += Number(m.max_marks || 100);
    map[cid].count += 1;
  });
  return classes
    .filter(c => map[c.id])
    .map(c => ({
      name: `${c.grade || ''} ${c.section || c.name}`.trim(),
      avgPct: map[c.id].maxTotal > 0
        ? Math.round((map[c.id].total / map[c.id].maxTotal) * 100)
        : 0,
      students: map[c.id].count
    }))
    .sort((a, b) => b.avgPct - a.avgPct);
};

// Compute grade distribution from marks array
const computeGradeDist = (marks) => {
  const counts = { A: 0, B: 0, C: 0, D: 0, F: 0 };
  marks.forEach(m => {
    const pct = m.max_marks > 0 ? (m.marks / m.max_marks) * 100 : 0;
    counts[gradeOf(pct)]++;
  });
  return Object.entries(counts)
    .filter(([, v]) => v > 0)
    .map(([grade, value]) => ({ name: `Grade ${grade}`, grade, value }));
};

// ── Tab panel helper ──────────────────────────────────────────────────────────
const TabPanel = ({ children, value, index }) =>
  value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

// ── Empty state helper ────────────────────────────────────────────────────────
const EmptyState = ({ msg }) => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
    <Typography color="text.secondary">{msg}</Typography>
  </Box>
);

// ════════════════════════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════════════════════════
const PrincipalAnalytics = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // ── Tab 1: Overview ─────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [classDistribution, setClassDistribution] = useState([]);
  const [financeData, setFinanceData] = useState(null);
  const [overviewTrend, setOverviewTrend] = useState([]);

  // ── Tab 2: Academic ─────────────────────────────────────────────────────────
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');
  const [examMarks, setExamMarks] = useState([]);
  const [examMarksLoading, setExamMarksLoading] = useState(false);
  const [examComparison, setExamComparison] = useState([]); // [{examName, avgPct}]

  // ── Tab 3: Attendance ───────────────────────────────────────────────────────
  const [attPeriod, setAttPeriod] = useState(30);
  const [attAnalytics, setAttAnalytics] = useState(null);
  const [attLoading, setAttLoading] = useState(false);
  const [attLoaded, setAttLoaded] = useState(false);

  // ── Tab 4: Student Profile ──────────────────────────────────────────────────
  const [selectedClassId, setSelectedClassId] = useState('');
  const [classStudents, setClassStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentMarks, setStudentMarks] = useState([]);
  const [studentAttendance, setStudentAttendance] = useState(null);
  const [studentLoading, setStudentLoading] = useState(false);

  // ── Tab 5: Chatbot Analytics ─────────────────────────────────────────────────
  const [chatbotAnalytics, setChatbotAnalytics] = useState(null);
  const [chatbotLoading, setChatbotLoading] = useState(false);
  const [chatbotLoaded, setChatbotLoaded] = useState(false);

  // ── Tab 6: Daycare Analytics ─────────────────────────────────────────────────
  const [daycareAnalytics, setDaycareAnalytics] = useState(null);
  const [daycareLoading, setDaycareLoading] = useState(false);
  const [daycareLoaded, setDaycareLoaded] = useState(false);

  // ── Initial data load ───────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [dashRes, classesRes, studentsRes, examsRes] = await Promise.all([
          principalAPI.getDashboard(),
          principalAPI.getClasses(),
          principalAPI.getStudents(),
          principalAPI.getExams()
        ]);

        let financeRes = null;
        try { financeRes = await principalAPI.getFinanceDashboard(); } catch (_) {}

        let trendRes = null;
        try {
          trendRes = await principalAPI.getAttendanceAnalytics({
            start_date: nDaysAgo(29),
            end_date: today()
          });
        } catch (_) {}

        if (!alive) return;

        const dashStats = dashRes.data.stats;
        const classesData = classesRes.data.classes || [];
        const studentsData = studentsRes.data.students || [];
        const examsData = examsRes.data.exams || [];

        setStats(dashStats);
        setClasses(classesData);
        setExams(examsData);

        // Class distribution
        setClassDistribution(
          classesData.map(cls => {
            const count = studentsData.filter(s => s.class_id === cls.id).length;
            return {
              name: `${cls.grade || 'Class'} ${cls.section || cls.name}`,
              value: count
            };
          }).filter(d => d.value > 0)
        );

        // Finance
        if (financeRes?.data) {
          const sum = financeRes.data.data?.summary || financeRes.data.summary || financeRes.data;
          setFinanceData({
            totalFees: Number(sum.totalFees || 0),
            totalCollected: Number(sum.totalCollected || 0),
            totalPending: Number(sum.totalPending || 0)
          });
        }

        // 30-day attendance trend
        if (trendRes?.data?.daily) {
          setOverviewTrend(
            trendRes.data.daily.map(d => ({
              date: formatDate(d.date),
              percentage: d.percentage,
              present: d.present,
              total: d.total
            }))
          );
        }

        // Pre-select latest exam for Tab 2
        if (examsData.length > 0) {
          setSelectedExamId(examsData[0].id);
        }
      } catch (err) {
        console.error('Analytics load error:', err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  // ── Load marks when selected exam changes ───────────────────────────────────
  useEffect(() => {
    if (!selectedExamId) return;
    let alive = true;
    const load = async () => {
      setExamMarksLoading(true);
      try {
        const res = await principalAPI.getMarks({ exam_id: selectedExamId });
        if (alive) setExamMarks(res.data.marks || []);
      } catch (_) {
        if (alive) setExamMarks([]);
      } finally {
        if (alive) setExamMarksLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [selectedExamId]);

  // ── Load exam comparison (last 5 exams) when Tab 2 first opens ─────────────
  useEffect(() => {
    if (activeTab !== 1 || exams.length === 0 || examComparison.length > 0) return;
    let alive = true;
    const load = async () => {
      const last5 = exams.slice(0, 5).reverse(); // oldest first for chart
      const results = await Promise.all(
        last5.map(e =>
          principalAPI.getMarks({ exam_id: e.id })
            .then(r => {
              const marks = r.data.marks || [];
              const total = marks.reduce((s, m) => s + Number(m.marks || 0), 0);
              const maxTotal = marks.reduce((s, m) => s + Number(m.max_marks || 100), 0);
              return {
                name: e.name,
                avgPct: maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0
              };
            })
            .catch(() => ({ name: e.name, avgPct: 0 }))
        )
      );
      if (alive) setExamComparison(results);
    };
    load();
    return () => { alive = false; };
  }, [activeTab, exams, examComparison.length]);

  // ── Load attendance analytics when Tab 3 opens or period changes ────────────
  const loadAttendance = useCallback(async (period) => {
    setAttLoading(true);
    try {
      const res = await principalAPI.getAttendanceAnalytics({
        start_date: nDaysAgo(period - 1),
        end_date: today()
      });
      setAttAnalytics(res.data);
      setAttLoaded(true);
    } catch (_) {
      setAttAnalytics(null);
    } finally {
      setAttLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 2) loadAttendance(attPeriod);
  }, [activeTab, attPeriod, loadAttendance]);

  // ── Load students when class is selected (Tab 4) ────────────────────────────
  useEffect(() => {
    if (!selectedClassId) { setClassStudents([]); setSelectedStudentId(''); return; }
    let alive = true;
    setStudentsLoading(true);
    principalAPI.getStudents({ class_id: selectedClassId })
      .then(r => { if (alive) setClassStudents(r.data.students || []); })
      .catch(() => { if (alive) setClassStudents([]); })
      .finally(() => { if (alive) setStudentsLoading(false); });
    return () => { alive = false; };
  }, [selectedClassId]);

  // ── Load student profile when student is selected ───────────────────────────
  useEffect(() => {
    if (!selectedStudentId) { setStudentMarks([]); setStudentAttendance(null); return; }
    let alive = true;
    setStudentLoading(true);
    Promise.all([
      principalAPI.getMarks({ student_id: selectedStudentId }),
      principalAPI.getStudentAttendanceHistory(selectedStudentId, {
        start_date: nDaysAgo(89),
        end_date: today()
      })
    ])
      .then(([marksRes, attRes]) => {
        if (!alive) return;
        setStudentMarks(marksRes.data.marks || []);
        setStudentAttendance(attRes.data || null);
      })
      .catch(() => { if (alive) { setStudentMarks([]); setStudentAttendance(null); } })
      .finally(() => { if (alive) setStudentLoading(false); });
    return () => { alive = false; };
  }, [selectedStudentId]);

  // ── Load chatbot analytics when Tab 5 opens ──────────────────────────────────
  useEffect(() => {
    if (activeTab !== 4 || chatbotLoaded) return;
    let alive = true;
    setChatbotLoading(true);
    chatbotAPI.getAnalytics()
      .then(r => { if (alive) setChatbotAnalytics(r.data.analytics || null); })
      .catch(() => { if (alive) setChatbotAnalytics(null); })
      .finally(() => { if (alive) { setChatbotLoading(false); setChatbotLoaded(true); } });
    return () => { alive = false; };
  }, [activeTab, chatbotLoaded]);

  // ── Load daycare analytics when Tab 6 opens ──────────────────────────────────
  useEffect(() => {
    if (activeTab !== 5 || daycareLoaded) return;
    let alive = true;
    setDaycareLoading(true);
    principalAPI.daycare.getAnalytics()
      .then(r => { if (alive) setDaycareAnalytics(r.data || null); })
      .catch(() => { if (alive) setDaycareAnalytics(null); })
      .finally(() => { if (alive) { setDaycareLoading(false); setDaycareLoaded(true); } });
    return () => { alive = false; };
  }, [activeTab, daycareLoaded]);

  // ── Derived data for Tab 2 ──────────────────────────────────────────────────
  const subjectPerf = examMarks.length > 0 ? computeSubjectPerf(examMarks) : [];
  const classPerf = examMarks.length > 0 ? computeClassPerf(examMarks, classes) : [];
  const gradeDist = examMarks.length > 0 ? computeGradeDist(examMarks) : [];

  // ── Derived data for Tab 4 ──────────────────────────────────────────────────
  const studentExamTrajectory = (() => {
    if (!studentMarks.length || !exams.length) return [];
    const examMap = {};
    exams.forEach(e => { examMap[e.id] = e.name; });
    const byExam = {};
    studentMarks.forEach(m => {
      const name = examMap[m.exam_id] || m.exam_id;
      if (!byExam[name]) byExam[name] = { total: 0, maxTotal: 0 };
      byExam[name].total += Number(m.marks || 0);
      byExam[name].maxTotal += Number(m.max_marks || 100);
    });
    return Object.entries(byExam).map(([name, d]) => ({
      name,
      avgPct: d.maxTotal > 0 ? Math.round((d.total / d.maxTotal) * 100) : 0
    }));
  })();

  const studentSubjectBreakdown = (() => {
    if (!studentMarks.length) return [];
    const map = {};
    studentMarks.forEach(m => {
      const subj = m.class_subjects?.subject_name || 'Unknown';
      if (!map[subj] || Number(m.marks) > map[subj].marks) {
        map[subj] = { marks: Number(m.marks || 0), max: Number(m.max_marks || 100) };
      }
    });
    return Object.entries(map).map(([name, d]) => ({
      name,
      pct: d.max > 0 ? Math.round((d.marks / d.max) * 100) : 0,
      marks: d.marks,
      max: d.max
    })).sort((a, b) => b.pct - a.pct);
  })();

  const studentAvgScore = studentMarks.length > 0
    ? (() => {
        const total = studentMarks.reduce((s, m) => s + Number(m.marks || 0), 0);
        const max = studentMarks.reduce((s, m) => s + Number(m.max_marks || 100), 0);
        return max > 0 ? Math.round((total / max) * 100) : 0;
      })()
    : null;

  const bestSubject = studentSubjectBreakdown.length > 0 ? studentSubjectBreakdown[0].name : null;

  // ── Loading screen ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const collectionRate = financeData?.totalFees > 0
    ? Math.round((financeData.totalCollected / financeData.totalFees) * 100)
    : 0;

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Analytics & Reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Detailed insights across students, subjects, attendance and finances
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/principal/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, p: 0.75 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{
            minHeight: 0,
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              minHeight: 0,
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
              },
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }
            }
          }}
        >
          <Tab label="Overview" icon={<Dashboard fontSize="small" />} iconPosition="start" />
          <Tab label="Academic" icon={<School fontSize="small" />} iconPosition="start" />
          <Tab label="Attendance" icon={<BarChartIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Student Profile" icon={<Person fontSize="small" />} iconPosition="start" />
          <Tab label="Chatbot" icon={<SmartToyIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Daycare" icon={<DaycareIcon fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* ──────────────── TAB 1: OVERVIEW ──────────────────────────────────── */}
      <TabPanel value={activeTab} index={0}>
        {/* Stat cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { label: 'Total Teachers', value: stats?.totalTeachers || 0, bg: 'linear-gradient(135deg,#667eea,#764ba2)' },
            { label: 'Total Students', value: stats?.totalStudents || 0, bg: 'linear-gradient(135deg,#f093fb,#f5576c)' },
            { label: 'Total Classes', value: stats?.totalClasses || 0, bg: 'linear-gradient(135deg,#4facfe,#00f2fe)' },
            { label: 'Attendance Today', value: `${stats?.attendanceToday || 0}%`, bg: 'linear-gradient(135deg,#fa709a,#fee140)' }
          ].map(({ label, value, bg }) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Card elevation={0} sx={{ background: bg, color: 'white' }}>
                <CardContent>
                  <Typography color="rgba(255,255,255,0.9)" variant="caption" fontWeight={500}>{label}</Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          {/* Class distribution */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Class-wise Student Distribution</Typography>
              {classDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={classDistribution}
                      cx="50%" cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {classDistribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <EmptyState msg="No class data available" />}
            </Paper>
          </Grid>

          {/* Fee overview */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccountBalance sx={{ color: '#667eea' }} />
                <Typography variant="h6" fontWeight="bold">Fee Collection Overview</Typography>
              </Box>
              {financeData?.totalFees > 0 ? (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Total Fees</Typography>
                      <Typography variant="h5" fontWeight="bold">{formatCurrency(financeData.totalFees)}</Typography>
                    </Box>
                    <Chip
                      label={`${collectionRate}% Collected`}
                      color={collectionRate >= 75 ? 'success' : collectionRate >= 50 ? 'warning' : 'error'}
                      icon={collectionRate >= 50 ? <TrendingUp /> : <TrendingDown />}
                    />
                  </Box>
                  <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="text.secondary">Collection Progress</Typography>
                      <Typography variant="body2" fontWeight="bold">{collectionRate}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={collectionRate}
                      sx={{
                        height: 12, borderRadius: 6, bgcolor: '#f0f0f0',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 6,
                          background: collectionRate >= 75
                            ? 'linear-gradient(90deg,#4caf50,#66bb6a)'
                            : collectionRate >= 50
                            ? 'linear-gradient(90deg,#ff9800,#ffb74d)'
                            : 'linear-gradient(90deg,#f44336,#ef5350)'
                        }
                      }}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#e8f5e9' }}>
                        <Typography variant="caption" color="text.secondary">Collected</Typography>
                        <Typography variant="h6" fontWeight="bold" color="success.main">{formatCurrency(financeData.totalCollected)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#ffebee' }}>
                        <Typography variant="caption" color="text.secondary">Pending</Typography>
                        <Typography variant="h6" fontWeight="bold" color="error.main">{formatCurrency(financeData.totalPending)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ) : <EmptyState msg="No fee data available" />}
            </Paper>
          </Grid>

          {/* 30-day attendance trend */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>30-Day Attendance Trend</Typography>
              {overviewTrend.length > 0 && overviewTrend.some(d => d.total > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={overviewTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 100]} tickFormatter={fmtPct} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload;
                        return (
                          <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, boxShadow: 2, border: '1px solid #eee' }}>
                            <Typography variant="body2" fontWeight="bold">{label}</Typography>
                            <Typography variant="body2" color="primary">{d?.percentage}% Attendance</Typography>
                            <Typography variant="caption" color="text.secondary">{d?.present} / {d?.total} present</Typography>
                          </Box>
                        );
                      }}
                    />
                    <Line type="monotone" dataKey="percentage" stroke="#667eea" strokeWidth={2} dot={false} name="Attendance %" />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyState msg="No attendance data in the last 30 days" />}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ──────────────── TAB 2: ACADEMIC PERFORMANCE ──────────────────────── */}
      <TabPanel value={activeTab} index={1}>
        {/* Exam selector */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Select Exam</InputLabel>
            <Select
              value={selectedExamId}
              label="Select Exam"
              onChange={e => setSelectedExamId(e.target.value)}
            >
              {exams.map(e => (
                <MenuItem key={e.id} value={e.id}>{e.name} ({e.academic_year})</MenuItem>
              ))}
            </Select>
          </FormControl>
          {examMarksLoading && <CircularProgress size={20} />}
        </Box>

        {exams.length === 0 ? (
          <Alert severity="info">No exams found. Create exams first to see academic analytics.</Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Subject performance */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Subject-wise Average Score</Typography>
                {subjectPerf.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={subjectPerf} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={fmtPct} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Avg Score']} />
                      <Bar dataKey="avgPct" name="Avg Score %" radius={[0, 6, 6, 0]}>
                        {subjectPerf.map((entry, i) => (
                          <Cell key={i} fill={perfColor(entry.avgPct)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg={examMarksLoading ? 'Loading...' : 'No marks data for this exam'} />}
              </Paper>
            </Grid>

            {/* Grade distribution */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Grade Distribution</Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  A ≥90% · B ≥75% · C ≥50% · D ≥35% · F &lt;35%
                </Typography>
                {gradeDist.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={gradeDist}
                        cx="50%" cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                        labelLine={false}
                      >
                        {gradeDist.map((entry, i) => (
                          <Cell key={i} fill={GRADE_COLORS[entry.grade] || COLORS[i]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No marks data" />}
              </Paper>
            </Grid>

            {/* Class performance ranking */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <EmojiEvents sx={{ color: '#ffc107' }} />
                  <Typography variant="h6" fontWeight="bold">Class Performance Ranking</Typography>
                </Box>
                {classPerf.length > 0 ? (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Rank</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell align="right">Avg Score</TableCell>
                          <TableCell align="right">Entries</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {classPerf.map((cls, i) => (
                          <TableRow key={cls.name}>
                            <TableCell>
                              <Typography fontWeight="bold" color={i === 0 ? '#ffc107' : i === 1 ? '#9e9e9e' : i === 2 ? '#cd7f32' : 'text.primary'}>
                                #{i + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>{cls.name}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${cls.avgPct}%`}
                                size="small"
                                sx={{ bgcolor: perfColor(cls.avgPct), color: 'white', fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>{cls.students}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : <EmptyState msg="No class data" />}
              </Paper>
            </Grid>

            {/* Exam comparison over time */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Exam Score Trend (Last 5 Exams)</Typography>
                {examComparison.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={examComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tickFormatter={fmtPct} />
                      <Tooltip formatter={(v) => [`${v}%`, 'School Avg']} />
                      <Line type="monotone" dataKey="avgPct" stroke="#764ba2" strokeWidth={2} dot={{ fill: '#764ba2', r: 5 }} name="School Avg %" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* ──────────────── TAB 3: ATTENDANCE ANALYTICS ──────────────────────── */}
      <TabPanel value={activeTab} index={2}>
        {/* Period selector */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Typography variant="body2" color="text.secondary">Period:</Typography>
          <ToggleButtonGroup
            value={attPeriod}
            exclusive
            onChange={(_, v) => { if (v) setAttPeriod(v); }}
            size="small"
          >
            <ToggleButton value={7}>Last 7 Days</ToggleButton>
            <ToggleButton value={30}>Last 30 Days</ToggleButton>
          </ToggleButtonGroup>
          {attLoading && <CircularProgress size={20} />}
        </Box>

        {attLoading && !attLoaded ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            {/* School-wide daily trend */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  School Attendance Trend ({attPeriod === 7 ? 'Last 7 Days' : 'Last 30 Days'})
                </Typography>
                {attAnalytics?.daily?.length > 0 && attAnalytics.daily.some(d => d.total > 0) ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={attAnalytics.daily.map(d => ({ ...d, date: formatDate(d.date) }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tickFormatter={fmtPct} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, boxShadow: 2, border: '1px solid #eee' }}>
                              <Typography variant="body2" fontWeight="bold">{label}</Typography>
                              <Typography variant="body2" color="primary">{d?.percentage}%</Typography>
                              <Typography variant="caption" color="text.secondary">{d?.present} / {d?.total} present</Typography>
                            </Box>
                          );
                        }}
                      />
                      <Line type="monotone" dataKey="percentage" stroke="#4facfe" strokeWidth={2} dot={false} name="Attendance %" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No attendance data for this period" />}
              </Paper>
            </Grid>

            {/* Class-wise comparison */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Class-wise Attendance Comparison</Typography>
                {attAnalytics?.byClass?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={attAnalytics.byClass} margin={{ left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="class_name" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tickFormatter={fmtPct} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Avg Attendance']} />
                      <Bar dataKey="percentage" name="Attendance %" radius={[6, 6, 0, 0]}>
                        {attAnalytics.byClass.map((entry, i) => (
                          <Cell key={i} fill={perfColor(entry.percentage)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No class attendance data" />}
              </Paper>
            </Grid>

            {/* At-risk students */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Warning sx={{ color: '#f44336' }} />
                  <Typography variant="h6" fontWeight="bold">At-Risk Students (&lt;75% Attendance)</Typography>
                </Box>
                {attAnalytics?.lowAttendance?.length > 0 ? (
                  <TableContainer sx={{ maxHeight: 320, overflowX: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Student</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell align="right">Attendance</TableCell>
                          <TableCell align="right">Present/Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attAnalytics.lowAttendance.map(s => (
                          <TableRow key={s.student_id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{s.admission_number}</Typography>
                            </TableCell>
                            <TableCell>{s.class_name}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${s.percentage}%`}
                                size="small"
                                color={s.percentage < 50 ? 'error' : 'warning'}
                                sx={{ fontWeight: 'bold' }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'text.secondary' }}>
                              {s.present}/{s.total}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={200} gap={1}>
                    <Typography variant="h5">✓</Typography>
                    <Typography color="success.main" fontWeight={500}>All students above 75% attendance</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* ──────────────── TAB 4: STUDENT PROFILE ───────────────────────────── */}
      <TabPanel value={activeTab} index={3}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Class</InputLabel>
              <Select
                value={selectedClassId}
                label="Select Class"
                onChange={e => { setSelectedClassId(e.target.value); setSelectedStudentId(''); }}
              >
                {classes.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {`${c.grade || ''} ${c.section || c.name}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small" disabled={!selectedClassId || studentsLoading}>
              <InputLabel>{studentsLoading ? 'Loading...' : 'Select Student'}</InputLabel>
              <Select
                value={selectedStudentId}
                label={studentsLoading ? 'Loading...' : 'Select Student'}
                onChange={e => setSelectedStudentId(e.target.value)}
              >
                {classStudents.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {studentLoading && (
            <Grid item xs={12} sm="auto" display="flex" alignItems="center">
              <CircularProgress size={20} />
            </Grid>
          )}
        </Grid>

        {!selectedStudentId ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300} gap={2}>
            <Person sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography color="text.secondary">Select a class and student to view their profile</Typography>
          </Box>
        ) : studentLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            {/* Summary cards */}
            {[
              { label: 'Exams Taken', value: [...new Set(studentMarks.map(m => m.exam_id))].length, color: '#667eea' },
              { label: 'Avg Score', value: studentAvgScore !== null ? `${studentAvgScore}%` : '—', color: perfColor(studentAvgScore || 0) },
              { label: 'Attendance', value: studentAttendance?.summary ? `${studentAttendance.summary.percentage}%` : '—', color: perfColor(studentAttendance?.summary?.percentage || 0) },
              { label: 'Best Subject', value: bestSubject || '—', color: '#4facfe', small: true }
            ].map(({ label, value, color, small }) => (
              <Grid item xs={6} md={3} key={label}>
                <Card elevation={1} sx={{ borderLeft: `4px solid ${color}` }}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant={small ? 'body1' : 'h5'} fontWeight="bold" sx={{ color, mt: 0.5 }}>
                      {value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Marks trajectory */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Exam Score Trajectory</Typography>
                {studentExamTrajectory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={studentExamTrajectory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tickFormatter={fmtPct} />
                      <Tooltip formatter={(v) => [`${v}%`, 'Score']} />
                      <Line type="monotone" dataKey="avgPct" stroke="#667eea" strokeWidth={2} dot={{ fill: '#667eea', r: 5 }} name="Score %" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No marks recorded" />}
              </Paper>
            </Grid>

            {/* Attendance summary */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Attendance (Last 90 Days)</Typography>
                {studentAttendance?.summary ? (
                  <Box>
                    <Box display="flex" justifyContent="center" mb={2}>
                      <Box sx={{
                        width: 100, height: 100, borderRadius: '50%',
                        background: `conic-gradient(${perfColor(studentAttendance.summary.percentage)} ${studentAttendance.summary.percentage * 3.6}deg, #eee 0deg)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <Box sx={{ width: 70, height: 70, borderRadius: '50%', bgcolor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography variant="h6" fontWeight="bold">{studentAttendance.summary.percentage}%</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Grid container spacing={1}>
                      {[
                        { label: 'Present', val: studentAttendance.summary.present, color: '#4caf50' },
                        { label: 'Absent', val: studentAttendance.summary.absent, color: '#f44336' },
                        { label: 'Excused', val: studentAttendance.summary.excused, color: '#9c27b0' }
                      ].map(({ label, val, color }) => (
                        <Grid item xs={6} key={label}>
                          <Box sx={{ p: 1, borderRadius: 1, bgcolor: '#f5f5f5', textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body1" fontWeight="bold" sx={{ color }}>{val}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ) : <EmptyState msg="No attendance data" />}
              </Paper>
            </Grid>

            {/* Subject breakdown */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Subject-wise Performance (Best Score per Subject)</Typography>
                {studentSubjectBreakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={studentSubjectBreakdown} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={fmtPct} />
                      <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, boxShadow: 2, border: '1px solid #eee' }}>
                              <Typography variant="body2" fontWeight="bold">{d?.name}</Typography>
                              <Typography variant="body2">{d?.marks} / {d?.max} marks ({d?.pct}%)</Typography>
                            </Box>
                          );
                        }}
                      />
                      <Bar dataKey="pct" name="Score %" radius={[0, 6, 6, 0]}>
                        {studentSubjectBreakdown.map((entry, i) => (
                          <Cell key={i} fill={perfColor(entry.pct)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No subject data" />}
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* ──────────────── TAB 5: CHATBOT ANALYTICS ───────────────────────────── */}
      <TabPanel value={activeTab} index={4}>
        {chatbotLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : !chatbotAnalytics ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300} gap={2}>
            <SmartToyIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography color="text.secondary">No chatbot usage data yet. Start using Ask ClassTree to see analytics here.</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Summary cards */}
            {[
              { label: 'Queries Today', value: chatbotAnalytics.totalToday, bg: 'linear-gradient(135deg,#4F46E5,#3730A3)' },
              { label: 'This Week', value: chatbotAnalytics.totalWeek, bg: 'linear-gradient(135deg,#0ea5e9,#0369a1)' },
              { label: 'This Month', value: chatbotAnalytics.totalMonth, bg: 'linear-gradient(135deg,#10b981,#059669)' },
              { label: 'Est. AI Cost', value: `$${chatbotAnalytics.estimatedCostUsd}`, bg: 'linear-gradient(135deg,#f59e0b,#d97706)' }
            ].map(({ label, value, bg }) => (
              <Grid item xs={6} md={3} key={label}>
                <Card elevation={0} sx={{ background: bg, color: 'white' }}>
                  <CardContent>
                    <Typography color="rgba(255,255,255,0.85)" variant="caption" fontWeight={500}>{label}</Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Daily volume chart */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Daily Query Volume (Last 7 Days)</Typography>
                {chatbotAnalytics.dailyVolume?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={chatbotAnalytics.dailyVolume}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={(v) => [v, 'Queries']} />
                      <Bar dataKey="queries" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Queries" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No queries in the last 7 days" />}
              </Paper>
            </Grid>

            {/* Role breakdown */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Usage by Role</Typography>
                {chatbotAnalytics.roleBreakdown?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={chatbotAnalytics.roleBreakdown}
                        dataKey="count"
                        nameKey="role"
                        cx="50%" cy="50%"
                        outerRadius={90}
                        label={({ role, percent }) => `${role} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {chatbotAnalytics.roleBreakdown.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, name) => [v, name]} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No role data available" />}
              </Paper>
            </Grid>

            {/* Top questions */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Top Questions (Last 30 Days)</Typography>
                {chatbotAnalytics.topQuestions?.length > 0 ? (
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 100 }} align="center">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chatbotAnalytics.topQuestions.map((q, i) => (
                          <TableRow key={i} hover>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" fontWeight={700}>{i + 1}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ maxWidth: 600 }}>{q.question}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={q.count} size="small" color="primary" variant="outlined" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : <EmptyState msg="No question data yet" />}
              </Paper>
            </Grid>

            {/* Token usage */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'grey.50' }}>
                <SmartToyIcon color="primary" />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Total tokens used (last 30 days): {chatbotAnalytics.totalTokens.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cost estimate based on GPT-4o-mini pricing (~$0.60 / 1M tokens blended)
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      {/* ──────────────── TAB 7: DAYCARE ANALYTICS ───────────────────────────── */}
      <TabPanel value={activeTab} index={5}>
        {daycareLoading ? (
          <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
        ) : !daycareAnalytics ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300} gap={2}>
            <DaycareIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography color="text.secondary">No daycare activity data yet.</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" color="error" startIcon={<AccountBalance />} onClick={() => navigate('/principal/daycare-dues')}>
                Manage Dues &amp; Record Payments
              </Button>
            </Grid>
            {/* Summary cards */}
            {[
              { label: 'Active Enrollments', value: daycareAnalytics.activeEnrollments, bg: 'linear-gradient(135deg,#4F46E5,#3730A3)' },
              { label: 'Total Billed', value: formatCurrency(daycareAnalytics.finance?.totalBilled || 0), bg: 'linear-gradient(135deg,#0ea5e9,#0369a1)' },
              { label: 'Total Collected', value: formatCurrency(daycareAnalytics.finance?.totalCollected || 0), bg: 'linear-gradient(135deg,#10b981,#059669)' },
              { label: 'Outstanding Dues', value: formatCurrency(daycareAnalytics.finance?.totalOutstanding || 0), bg: 'linear-gradient(135deg,#f59e0b,#d97706)' }
            ].map(({ label, value, bg }) => (
              <Grid item xs={6} md={3} key={label}>
                <Card elevation={0} sx={{ background: bg, color: 'white' }}>
                  <CardContent>
                    <Typography color="rgba(255,255,255,0.85)" variant="caption" fontWeight={500}>{label}</Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>{value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Monthly hours/billing trend */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Monthly Billed Hours</Typography>
                {daycareAnalytics.sessionTrend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={daycareAnalytics.sessionTrend.map(t => ({
                      month: formatDate(t.month),
                      hours: Number(t.hours || 0),
                      students: Number(t.students || 0)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="hours" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Billed Hours" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No completed daycare sessions yet" />}
              </Paper>
            </Grid>

            {/* Monthly collection trend */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Monthly Collection</Typography>
                {daycareAnalytics.finance?.monthlyTrend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={daycareAnalytics.finance.monthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip formatter={(v) => `₹${v}`} />
                      <Line type="monotone" dataKey="collected" stroke="#10b981" name="Collected (₹)" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <EmptyState msg="No daycare payments recorded yet" />}
              </Paper>
            </Grid>
          </Grid>
        )}
      </TabPanel>
    </Container>
  );
};

export default PrincipalAnalytics;
